import { AngularFireAuth } from '@angular/fire/auth';
import { AngularFireAnalytics } from '@angular/fire/analytics';
import { AngularFireFunctions } from '@angular/fire/functions';
import { environment } from './../../../environments/environment';
import { Injectable } from '@angular/core';
import { ToastController, AlertController, ActionSheetController, LoadingController, ModalController } from '@ionic/angular';
import { OpenModalGuardService } from '../open-modal-guard/open-modal-guard.service';
import { ErrorMonitoringService } from '../error-monitoring/error-monitoring.service';
import { CustomAlertComponent } from '../../components/custom-alert/custom-alert.component';
import { ClipboardService } from 'ngx-clipboard';
import * as moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class HelperService {

  loading: any;

  constructor(
    private toastController: ToastController,
    private alertController: AlertController,
    private actionSheetController: ActionSheetController,
    private loadingController: LoadingController,
    private openModalGuardService: OpenModalGuardService,
    private modalController: ModalController,
    private clipboardService: ClipboardService,
    private errorMonitoringService: ErrorMonitoringService,
    private angularFireFunctions: AngularFireFunctions,
    private firebaseAnalytics: AngularFireAnalytics,
    private angularFireAuth: AngularFireAuth
  ) { }

  // Presenta un toast
  async presentToast(params: any) {
    const toast = await this.toastController.create(params);
    toast.present();
  }

  // Presenta un toast con boton de acción y un metodo para ejecutar cuando se cierre
  async presentToastWithOptions(params: any, method: any) {
    const toast = await this.toastController.create(params);
    toast.present();
    toast.onDidDismiss().then(() => {
      method();
    });
  }

  // Presenta un alert con un mensaje de alerta
  async presentMessageAlert(title: string, message: string, subtitle: string = '', icon: string = 'alerta', size: string = 'small') {
    // indica que se debe bloquear/desbloquear la navegación
    this.openModalGuardService.setModalOpen(true);
    // crea el modal de alerta para logerse o registrarse
    const loginModalAlert = await this.modalController.create({
      component: CustomAlertComponent,
      cssClass: `custom-alert-${size}`,
      componentProps: {
        icon,
        title,
        subtitle,
        message
      },
      backdropDismiss: false,
    });
    loginModalAlert.onDidDismiss().then(data => {
      this.openModalGuardService.setModalOpen(false);
    });
    // presenta el modal con la alerta de login
    return await loginModalAlert.present();
  }

  // presenta una lista para seleccionar una opcion
  async presentRadioSelectAlert(title: string, subtitle: string, inputs: Array<any>, buttons: Array<any>) {
    const alert = await this.alertController.create({
      header: title,
      inputs: inputs,
      buttons: buttons
    });
    await alert.present();
  }

  // presenta un alert de confirmacion
  async presentAlertConfirm(header: string, message: string, buttons: Array<any>) {
    const alert = await this.alertController.create({
      header: header,
      subHeader: message,
      buttons: buttons
    });

    await alert.present();
  }

  // Presenta un action sheet
  async presentActionSheet(header: string, buttons: Array<any>) {
    const actionSheet = await this.actionSheetController.create({
      header: header,
      buttons: buttons
    });
    await actionSheet.present();
  }

  // presenta el componente de carga
  async presentLoading() {
    this.loading = await this.loadingController.create({
      duration: 10000
    });
    await this.loading.present();
  }

  // copia un texto en clipboard en pc y abre la api de share en el mobile
  presentShare(text: string, url: string) {
    let navigatorWrapper: any;
    navigatorWrapper = window.navigator;
    if (navigatorWrapper && navigatorWrapper.share) {
      navigatorWrapper.share({
        text,
        url
      })
      .then( () => {
        // registra el evento en analytics
        this.firebaseAnalytics.logEvent('share', {
          method: 'navigator'
        });
      })
      .catch((error) => {
        this.errorMonitoringService.sendError(error, 'share');
      });
    } else {
      this.clipboardService.copyFromContent(`${text}\n ${url}`);
      // avisa al usuario que fue copiado el mensaje
      this.presentMessageAlert(
        'Compartir',
        'El mensaje ha sido copiado al portapapeles',
        '',
        'check'
      );
      // registra el evento en analytics
      this.firebaseAnalytics.logEvent('share', {
        method: 'clipboard'
      });
    }
  }

  // validate share api
  validateShareApi(): boolean {
    let navigatorWrapper: any;
    navigatorWrapper = window.navigator;
    if (navigatorWrapper && navigatorWrapper.share) {
      return true;
    }
    return false;
  }

  // calcula los días a una fecha
  calculateFromDays(date: any) {
    return moment(date).locale('es').fromNow();
  }

  // calcula los días a una fecha
  calculateToDays(date: any) {
    return moment(date).locale('es').toNow();
  }

  // open whats app
  openWhatsApp(text = '') {
    window.open(`${environment.wsChat}?text=${text}`);
  }

  // oculta el componente de carga
  dismissLoading() {
    if (this.loading) {
      this.loading.dismiss();
    }
  }

  // recarga la página
  reloadPage() {
    location.reload();
  }

  // Logout
  logout() {
    this.angularFireAuth.signOut().then( () => {
      this.reloadPage();
    }).catch( error => {
      this.errorMonitoringService.sendError(error, 'logout');
      this.presentMessageAlert('Mensaje', 'No pudimos cerrar la sesión, inténtalo de nuevo');
    });
  }

  /** Llama una función */
  callFunction(name: string, data: any) {
    const callable = this.angularFireFunctions.httpsCallable(name);
    return callable(data);
  }

}
